import type { ApiError } from "./types";
import { recordApiError } from "@/lib/diagnostics";

export interface ApiRequestOptions {
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

function classify(status: number): ApiError["code"] {
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  if (status === 429) return "rate_limited";
  if (status >= 500) return "server_error";
  return undefined;
}

export class ApiClient {
  readonly baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
  }

  async get<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>("GET", path, undefined, opts);
  }

  async patch<T>(path: string, body: unknown, opts: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>("PATCH", path, body, opts);
  }

  async post<T>(path: string, body?: unknown, opts: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>("POST", path, body, opts);
  }

  async put<T>(path: string, body: unknown, opts: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>("PUT", path, body, opts);
  }

  async del<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>("DELETE", path, undefined, opts);
  }

  /** A binary read (a feedback report's screenshot, 2026-10-27): the bytes as a Blob. */
  async blob(path: string, opts: ApiRequestOptions = {}): Promise<Blob> {
    const res = await fetch(`${this.baseUrl}${path}`, { method: "GET", headers: { ...(opts.headers ?? {}) }, signal: opts.signal });
    if (!res.ok) {
      recordApiError("GET", path, res.status, classify(res.status), res.headers.get("apigw-requestid"));
      const err: ApiError = { status: res.status, message: `${res.status} ${res.statusText}`, code: classify(res.status) };
      throw err;
    }
    return await res.blob();
  }

  protected async request<T>(
    method: "GET" | "PATCH" | "POST" | "PUT" | "DELETE",
    path: string,
    body: unknown | undefined,
    opts: ApiRequestOptions,
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const headers: Record<string, string> = { ...(opts.headers ?? {}) };
    let init: RequestInit = { method, headers, signal: opts.signal };
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
      init = { ...init, body: JSON.stringify(body) };
    }
    let res: Response;
    try {
      res = await fetch(url, init);
    } catch (e) {
      // a read is tried once more when the network blinked ("Failed to fetch", 2026-10-24: whole pages stayed empty
      // after one lost request); a write never is, it may have been done and only its answer lost
      if (method !== "GET" || !(e instanceof TypeError) || opts.signal?.aborted) throw e;
      await new Promise((r) => setTimeout(r, 800));
      res = await fetch(url, init);
    }
    if (!res.ok) {
      let payload: unknown = undefined;
      try {
        payload = await res.json();
      } catch {
        // body was empty / non-JSON — leave payload undefined
      }
      const detail = (payload && typeof payload === "object" && "error" in (payload as object))
        ? String((payload as { error: unknown }).error)
        : "";
      recordApiError(method, path, res.status, classify(res.status), res.headers.get("apigw-requestid"));   // for feedback reports
      const err: ApiError = {
        status: res.status,
        message: detail || `${res.status} ${res.statusText}`,
        code: classify(res.status),
        body: payload,
      };
      throw err;
    }
    return (await res.json()) as T;
  }
}
